import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Phone, Mail, MapPin, CheckCircle2 } from "lucide-react";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      toast.success("Message received. We'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-16 md:py-20 grid md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Contact</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-semibold mt-3">
          Talk to our operations team.
        </h1>
        <p className="mt-6 text-slate-600 leading-relaxed">
          For portfolio onboarding, emergency dispatch, or vendor management
          questions — send a message below. For an itemized bid, use the quote
          form.
        </p>
        <div className="mt-10 space-y-4 text-sm">
          <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-slate-500" /> (973) 715-6578</div>
          <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-slate-500" /> services@meadpp.com</div>
          <div className="flex items-center gap-3"><MapPin className="h-4 w-4 text-slate-500" /> New Jersey · Pennsylvania</div>
        </div>
      </div>
      <div className="md:col-span-7">
        {done ? (
          <div data-testid="contact-success" className="border border-emerald-200 bg-emerald-50 p-8">
            <CheckCircle2 className="h-6 w-6 text-emerald-700" />
            <div className="font-display text-2xl mt-3 tracking-tight">Message received.</div>
            <p className="text-sm text-slate-600 mt-2">
              Our operations team will respond within one business day.
            </p>
            <button
              data-testid="contact-again-btn"
              onClick={() => setDone(false)}
              className="mt-6 inline-flex items-center px-4 py-2 border border-slate-300 text-sm font-medium rounded-sm hover:bg-white"
            >
              Send another
            </button>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="contact-form" className="border border-slate-200 bg-white p-6 md:p-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label="Full name" required id="name" value={form.name} onChange={update("name")} testId="contact-name" />
            <FormField label="Email" required id="email" type="email" value={form.email} onChange={update("email")} testId="contact-email" />
            <FormField label="Company" id="company" value={form.company} onChange={update("company")} testId="contact-company" />
            <FormField label="Phone" id="phone" value={form.phone} onChange={update("phone")} testId="contact-phone" />
            <div className="md:col-span-2">
              <FormField label="How can we help?" required id="message" as="textarea" rows={5} value={form.message} onChange={update("message")} testId="contact-message" />
            </div>
            <div className="md:col-span-2 flex items-center justify-between">
              <div className="text-xs text-slate-500">We respond within 1 business day.</div>
              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={loading}
                className="px-5 py-2.5 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white text-sm font-semibold rounded-sm disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send message"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export function FormField({ label, id, as, testId, className = "", ...props }) {
  const Tag = as || "input";
  return (
    <label htmlFor={id} className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">
        {label}
        {props.required && <span className="text-[#1E40AF] ml-1">*</span>}
      </span>
      <Tag
        id={id}
        data-testid={testId}
        {...props}
        className="w-full px-3 py-2 bg-white border border-slate-300 focus:border-[#1E40AF] focus:ring-2 focus:ring-[#1E40AF]/20 outline-none text-sm rounded-sm"
      />
    </label>
  );
}
